import { alpha, Theme } from '@mui/material';
import { ButtonProps, buttonClasses } from '@mui/material/Button';

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// VARIANTS
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
    link: true;
  }
}

export function button(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const linkVariant = ownerState.variant === 'link';

    const smallSize = ownerState.size === 'small';

    const mediumSize = ownerState.size === 'medium';

    const largeSize = ownerState.size === 'large';

    const defaultStyle = {
      textTransform: 'none',
      borderRadius: '12px',
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          color: theme.palette.common.black,
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // OUTLINED
        ...(outlinedVariant && {
          borderColor: theme.palette.grey[200],
          background: theme.palette.grey[25],
          boxShadow: '0px 1.222px 2.444px 0px rgba(16, 24, 40, 0.05)',
          '&:focus': {
            outline: 'none',
          },
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // TEXT
        ...(textVariant && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[200], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[200], 0.24),
          },
        }),
        // LINK
        ...(linkVariant && {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }),
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        // CONTAINED
        ...(containedVariant && {
          '&:hover': {
            boxShadow: theme.customShadows[color],
          },
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette[color][lightMode ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.32),
          },
        }),
        // LINK
        ...(linkVariant && {
          color: theme.palette[color].main,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }),
      }),
    }));

    const disabledState = {
      [`&.${buttonClasses.disabled}`]: {
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
      },
    };

    const size = {
      ...(smallSize && {
        height: 30,
        fontSize: 13,
        paddingLeft: 8,
        paddingRight: 8,
        ...((textVariant || linkVariant) && {
          paddingLeft: 4,
          paddingRight: 4,
        }),
      }),
      ...(mediumSize && {
        paddingLeft: 12,
        paddingRight: 12,
        ...((textVariant || linkVariant) && {
          paddingLeft: 8,
          paddingRight: 8,
        }),
      }),
      ...(largeSize && {
        height: 44,
        fontSize: 15,
        paddingLeft: 16,
        paddingRight: 16,
        ...((textVariant || linkVariant) && {
          paddingLeft: 10,
          paddingRight: 10,
        }),
      }),
    };

    return [defaultStyle, ...colorStyle, disabledState, size];
  };

  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => rootStyles(ownerState),
      },
    },
  };
}
